import {
    AnyPcbBakedNode,
    BakedRulesFor,
    FootPrintPadHoleType,
    getFirstTopLevelLayout,
    IPcbBoardLayersMap,
    PcbRuleSetsWithSpecificityMap,
    LayerOrientation,
    PcbBakedNode,
    PcbBoardLayer,
    PcbBoardLayerMaterials,
    PcbBoardLayerType,
    pcbLayoutRootNodeId,
    PcbLayoutRuleCompiler,
    PcbLayoutRuntimeRulesManager,
    PcbNodeHelper,
    PcbNodesMap,
    PcbNodeTypes,
    PcbRuleSetsMap,
    PcbRulesMap,
    PcbViaType,
    TransformBakedRules,
    DocumentService,
} from "@buildwithflux/core";
import {defaultLayerThickness, isCopperLayer, IVector3} from "@buildwithflux/models";
import {pickValues, Records} from "@buildwithflux/shared";
import {Color} from "three";

import {
    ColorSilkBottom,
    ColorSilkTop,
} from "components/pages/document/components/editor/components/pcb_editor/components/pcb_editor_canvas/nodes/silk_rectangle/hooks/abstractColorSilk";
import {getLayerColor, type DocumentStore} from "modules/document/state/document";
import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";
import {getAllBakedNodes} from "modules/stores/pcb/utils";
import {PcbNodeSelector} from "modules/reactivity/hooks";

const EMPTY_PCB_BOARD_LAYER_MAP: IPcbBoardLayersMap = {};
// NOTE: needs to be used with DEEP comparison for proper memoization
export const selectPcbLayoutNodeStackup = (bakedRules: BakedRulesFor<PcbNodeTypes.layout> | undefined) => {
    if (!bakedRules) return EMPTY_PCB_BOARD_LAYER_MAP;
    return bakedRules.stackup ?? EMPTY_PCB_BOARD_LAYER_MAP;
};

function getLayerThickness(
    pcbLayoutNodeLayers: PcbBoardLayer[],
    nodeLayer: LayerOrientation | string | undefined,
    material: PcbBoardLayerMaterials | undefined,
    type: PcbBoardLayerType | undefined,
) {
    // TODO: don't want to perform a search to obtain this - if calling context is react, can this be memo'd?
    const layer = pcbLayoutNodeLayers.find(
        (layer) =>
            layer.uid === nodeLayer ||
            (layer.orientation === nodeLayer &&
                ((material && layer.material === material) ||
                    (material === "Copper" && isCopperLayer(layer)) ||
                    (type && layer.type === type))),
    );

    return layer?.thickness || defaultLayerThickness;
}

// TODO: Reivew and check if we need to create cached selector
export const selectStackupCopperLayerThickness = (
    layoutBakedRules: BakedRulesFor<PcbNodeTypes.layout> | undefined,
    layerUid: string,
) => {
    if (!layoutBakedRules) {
        return getLayerThickness([], layerUid, "Copper", undefined);
    }

    const pcbLayoutNodeLayers = Object.values(layoutBakedRules?.stackup || {});
    return getLayerThickness(pcbLayoutNodeLayers, layerUid, "Copper", undefined);
};

// TODO: Reivew and check if we need to create cached selector
export const selectStackupCopperLayerColor = (
    layoutBakedRules: BakedRulesFor<PcbNodeTypes.layout> | undefined,
    layerUid: string,
) => {
    if (!layoutBakedRules) {
        return getLayerColor([], layerUid, "Copper", undefined);
    }

    const pcbLayoutNodeLayers = Object.values(layoutBakedRules?.stackup || {});

    return getLayerColor(pcbLayoutNodeLayers, layerUid, "Copper", undefined);
};

const DEFAULT_POSITION_RELATIVE_TO_DOCUMENT: IVector3 = {x: 0, y: 0, z: 0};
// TODO: Reivew and check if we need to create cached selector
export const selectPcbNodePositionRelativeToDocument = (node: AnyPcbBakedNode | undefined) => {
    return (
        (node?.bakedRules as TransformBakedRules)?.positionRelativeToDocument || DEFAULT_POSITION_RELATIVE_TO_DOCUMENT
    );
};

const EMPTY_PCB_RULES_MAP: PcbRulesMap = {};
export const selectNodeObjectSpecificRules = (node: AnyPcbBakedNode | undefined) => {
    return node?.pcbNodeRuleSet || EMPTY_PCB_RULES_MAP;
};

const EMPTY_RULE_SETS_FOR_NODE: PcbRuleSetsWithSpecificityMap = {};
/**
 * For a given object, determine the selector-based rules that apply to it.
 * NOTE: needs to be used with DEEP comparison for proper memoization, because
 * getRuleSetsForNode always returns new objects.
 * TODO: write tests and refactor... this function takes >1ms in manual testing
 */
export const selectRuleSetsForNode = (
    node: AnyPcbBakedNode | undefined,
    globalRules: PcbRuleSetsMap | undefined,
    documentService: DocumentService,
    documentStore: DocumentStore,
) => {
    const document = documentStore.getState().document;

    if (!document || !node) {
        return EMPTY_RULE_SETS_FOR_NODE;
    }

    // TODO: Review this, do we need to have `pcbLayoutRuleSets` in documentService also?
    // NOTE: we need to merge together any global rules from
    // redux with any rules generated during node baking. Any
    // change to a global rule should change the flattenedRules
    // of the node and cause this hook to fire.

    // HACK: we need to add the runtime rules, primarily
    // selector based rules for pads, back into the rules
    // manager where they go during baking, so they can be
    // plucked out by getRuleSetsForNode below.
    const runtimeRulesManager = new PcbLayoutRuntimeRulesManager();
    runtimeRulesManager.addRuntimePcbLayoutRules(globalRules ?? {});

    const pcbLayoutRuleSelectors = new PcbLayoutRuleCompiler(
        document.elements,
        document.nets,
        globalRules ?? {},
        getAllBakedNodes(documentService),
        document,
        runtimeRulesManager,
        FluxLogger,
    );

    const ruleSets = pcbLayoutRuleSelectors.getRuleSetsForNode(node.uid);

    if (Records.isEmpty(ruleSets)) {
        return EMPTY_RULE_SETS_FOR_NODE;
    }

    return ruleSets;
};

function findLayerColorByPredicate(pcbBoardLayers: IPcbBoardLayersMap, isMatch: (layer: PcbBoardLayer) => boolean) {
    for (const layerUid in pcbBoardLayers) {
        const layer = pcbBoardLayers[layerUid];
        if (layer && isMatch(layer)) {
            return new Color(layer.color);
        }
    }
}
export const selectSilkColor = (
    bakedRules: BakedRulesFor<PcbNodeTypes.layout> | undefined,
    layerUidOrName: string | undefined,
) => {
    const stackup = selectPcbLayoutNodeStackup(bakedRules);
    const color = findLayerColorByPredicate(stackup, (layer) => {
        if (layer.uid === layerUidOrName) {
            return true;
        }

        return layer.name === layerUidOrName;
    });

    if (color) {
        return color;
    }

    if (layerUidOrName === LayerOrientation.bottom) {
        return ColorSilkBottom;
    } else {
        return ColorSilkTop;
    }
};

export const selectHasActivePcbLayout = (pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>) => {
    const hasActiveNodes = Object.values(pcbLayoutNodes)
        .filter((node) => node.uid !== pcbLayoutRootNodeId)
        .some((node) => node.bakedRules?.active);
    return hasActiveNodes && !!getFirstTopLevelLayout(pcbLayoutNodes);
};

export const makeSelectNodeOfType = <T extends PcbNodeTypes>(type: T): PcbNodeSelector<undefined | PcbBakedNode<T>> => {
    return (node: AnyPcbBakedNode | undefined): undefined | PcbBakedNode<T> => {
        if (node?.type === type) return node as PcbBakedNode<T>;
        return undefined;
    };
};

function getSelectedLayoutNodes(
    pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>,
    selectedSubjectsUids: string[],
    documentStore: DocumentStore,
) {
    const document = documentStore.getState().document;
    return pickValues(pcbLayoutNodes, selectedSubjectsUids).filter(
        (nodeData) =>
            !(
                nodeData.type === PcbNodeTypes.root ||
                (nodeData.type === PcbNodeTypes.element && document?.elements[nodeData.uid]) ||
                (nodeData.type === PcbNodeTypes.net && document?.routes[nodeData.uid])
            ),
    );
}

// TODO: Memoized?
export const selectSelectedPcbLayoutNodesCount = (
    pcbNodes: PcbNodesMap<AnyPcbBakedNode>,
    selectedSubjectsUids: string[],
    documentStore: DocumentStore,
) => {
    const selectedNodesData = getSelectedLayoutNodes(pcbNodes, selectedSubjectsUids, documentStore);
    return selectedNodesData.length;
};

// NOTE: needs to be used with shallow comparison for proper memoization
// TODO: Memoized using re-reselect?
export const selectHoleNodes = (pcbNodes: PcbNodesMap<AnyPcbBakedNode>, includeStitchingVias = true) => {
    const result: Array<PcbBakedNode<PcbNodeTypes.pad | PcbNodeTypes.via>> = [];

    for (const holeNode of Records.iterate(pcbNodes)) {
        // QUESTION: Can we replace this with isInTreeIndex?
        // Old code: const isOrphan = !isInTreeIndex[holeNode.uid];
        const isOrphan = !pcbNodes[holeNode.uid];
        if (isOrphan) {
            // Many documents have orphaned nodes from old dead (or maybe even existing!) code.
            // Skip them, a) because they're irrelevant, b) because many have bad data, c) because
            // they're skipped in the bakedRules process, so won't be properly prepared for here.
            continue;
        }
        if (!holeNode.bakedRules?.active) {
            continue;
        }
        if (holeNode.type === PcbNodeTypes.pad) {
            if (
                holeNode.bakedRules.hole?.holeType === FootPrintPadHoleType.platedThroughHole ||
                holeNode.bakedRules.hole?.holeType === FootPrintPadHoleType.nonPlatedHole
            ) {
                result.push(holeNode);
            }
        } else if (holeNode.type === PcbNodeTypes.via) {
            const isStitchingVia = PcbNodeHelper.isStitchingNode(pcbNodes[holeNode.uid]);
            if (
                (includeStitchingVias || !isStitchingVia) &&
                // For now, we're only looking at through-hole vias to determine the layout board shape.
                // This might need to change to include micro, buried and blind vias if the requirements
                // for calculating the layout board shape need to be more precise.
                holeNode.bakedRules.viaType === PcbViaType.throughHole
            ) {
                result.push(holeNode);
            }
        }
    }

    return result;
};
